import EmployeesChart from "components/charts/modern/EmployeesChart";
import PayRatioHistoryChart from "components/charts/modern/PayRatioHistoryChart";
import SayOnPayChart from "components/charts/modern/SayOnPayChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import AmericanCompensationContent from "components/company/v3/american/contents/governance/AmericanCompensationContent";
import AmericanGovernanceContent from "components/company/v3/american/contents/governance/AmericanGovernanceContent";
import { CompanyGovernanceTabEnum } from "components/company/v3/menu/enums";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import {
  BarChartHorizontalBigIcon,
  BriefcaseBusinessIcon,
  DollarSignIcon,
  UserCheckIcon,
  UsersIcon,
} from "lucide-react";
import { useIntl } from "react-intl";

interface AmericanGovernanceBoxProps {
  tickerSymbol: string;
  isRenderPageDefault?: boolean;
  tab: CompanyGovernanceTabEnum;
  onChangeTab: (tab: CompanyGovernanceTabEnum) => void;
}

export default function AmericanGovernanceBox({
  tickerSymbol,
  isRenderPageDefault = false,
  tab,
  onChangeTab,
}: AmericanGovernanceBoxProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  if (!viewer?.isPremium) {
    return null;
  }

  return (
    <CompanyContainer
      className={classNames({
        "xl:h-full": [CompanyGovernanceTabEnum.EXECUTIVE, CompanyGovernanceTabEnum.COMPENSATION].includes(tab),
      })}
    >
      <CompanyContentContainer>
        <Tabs
          defaultValue={CompanyGovernanceTabEnum.EXECUTIVE}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyGovernanceTabEnum)}
          className="h-full w-full"
        >
          <CompanyTabsList
            tabs={[
              {
                value: CompanyGovernanceTabEnum.EXECUTIVE,
                label: intl.formatMessage({
                  defaultMessage: "Executivos",
                  description: "Aba do menu superior da empresa",
                  id: "DutxfM",
                }),
                icon: <UsersIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.COMPENSATION,
                label: intl.formatMessage({
                  defaultMessage: "Remuneração",
                  description: "Aba do menu superior da empresa",
                  id: "mD4xGc",
                }),
                icon: <DollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.SAY_ON_PAY,
                label: intl.formatMessage({
                  defaultMessage: "Voto na Remuneração",
                  description: "Aba do menu superior da empresa",
                  id: "W5C4M/",
                }),
                icon: <UserCheckIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.CEO_PAY_RATIO,
                label: intl.formatMessage({
                  defaultMessage: "Proporção Salarial do CEO",
                  description: "Aba do menu superior da empresa",
                  id: "nVRWUY",
                }),
                icon: <BarChartHorizontalBigIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyGovernanceTabEnum.EMPLOYEES,
                label: intl.formatMessage({
                  defaultMessage: "Empregados",
                  description: "Aba do menu superior da empresa",
                  id: "YuiKZP",
                }),
                icon: <BriefcaseBusinessIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
            ]}
          />

          <CompanyTabsContent value={CompanyGovernanceTabEnum.EXECUTIVE}>
            <AmericanGovernanceContent tickerSymbol={tickerSymbol} isRenderPageDefault={isRenderPageDefault} />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.COMPENSATION}>
            <AmericanCompensationContent tickerSymbol={tickerSymbol} isRenderPageDefault={isRenderPageDefault} />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.SAY_ON_PAY}>
            <SayOnPayChart />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.CEO_PAY_RATIO}>
            <PayRatioHistoryChart />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyGovernanceTabEnum.EMPLOYEES}>
            <EmployeesChart tickerSymbols={[tickerSymbol]} />
          </CompanyTabsContent>
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
